// Validation utilities
export const isEmail = (email) => {
  if (!email || typeof email !== 'string') return false;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const isPhoneNumber = (phone) => {
  if (!phone || typeof phone !== 'string') return false;
  const phoneRegex = /^[+]?[1-9][\d]{0,15}$/;
  return phoneRegex.test(phone.replace(/\s/g, ''));
};

export const isUrl = (url) => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

// Password validation
export const validatePassword = (password) => {
  const errors = [];
  
  if (password.length < 8) {
    errors.push('Password must be at least 8 characters long');
  }
  
  if (!/[A-Z]/.test(password)) {
    errors.push('Password must contain at least one uppercase letter');
  }
  
  if (!/[a-z]/.test(password)) {
    errors.push('Password must contain at least one lowercase letter');
  }
  
  if (!/\d/.test(password)) {
    errors.push('Password must contain at least one number');
  }
  
  if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
    errors.push('Password must contain at least one special character');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

// Encryption utilities
export const encryptData = (data, secretKey) => {
  if (!secretKey) {
    throw new Error('Secret key is required for encryption');
  }
  
  try {
    const jsonString = JSON.stringify(data);
    const keyChars = secretKey.split('').map(c => c.charCodeAt(0));
    
    let encrypted = '';
    for (let i = 0; i < jsonString.length; i++) {
      const charCode = jsonString.charCodeAt(i);
      const keyCode = keyChars[i % keyChars.length];
      const encryptedChar = charCode ^ keyCode;
      encrypted += ('0' + encryptedChar.toString(16)).slice(-2);
    }
    
    return encrypted;
  } catch (error) {
    return null;
  }
};

export const decryptData = (encryptedData, secretKey) => {
  if (!secretKey || !encryptedData) {
    return null;
  }
  
  try {
    const keyChars = secretKey.split('').map(c => c.charCodeAt(0));
    const hexPairs = encryptedData.match(/.{1,2}/g) || [];
    
    let decrypted = '';
    for (let i = 0; i < hexPairs.length; i++) {
      const encryptedChar = parseInt(hexPairs[i], 16);
      const keyCode = keyChars[i % keyChars.length];
      const originalChar = encryptedChar ^ keyCode;
      decrypted += String.fromCharCode(originalChar);
    }
    
    // Validate decrypted string before parsing to prevent code injection
    if (typeof decrypted !== 'string' || decrypted.trim() === '') {
      return null;
    }
    
    // Basic validation to ensure it looks like valid JSON
    const trimmed = decrypted.trim();
    if (!((trimmed.startsWith('{') && trimmed.endsWith('}')) || 
          (trimmed.startsWith('[') && trimmed.endsWith(']')) ||
          (trimmed.startsWith('"') && trimmed.endsWith('"')) ||
          /^(true|false|null|\d+(\.\d+)?)$/.test(trimmed))) {
      return null;
    }
    
    return JSON.parse(decrypted);
  } catch (error) {
    return null;
  }
};

// String utilities
export const capitalize = (str) => {
  if (!str || typeof str !== 'string') return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

export const truncate = (str, length) => {
  if (!str || typeof str !== 'string') return '';
  return str.length > length ? str.substring(0, length) + '...' : str;
};

// Array utilities
export const chunk = (array, size) => {
  const chunks = [];
  for (let i = 0; i < array.length; i += size) {
    chunks.push(array.slice(i, i + size));
  }
  return chunks;
};

export const unique = (array) => {
  return [...new Set(array)];
};

// Object utilities
export const deepClone = (obj) => {
  if (obj === null || typeof obj !== 'object') return obj;
  if (obj instanceof Date) return new Date(obj.getTime());
  if (obj instanceof Array) return obj.map(item => deepClone(item));
  if (typeof obj === 'object') {
    const clonedObj = {};
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        clonedObj[key] = deepClone(obj[key]);
      }
    }
    return clonedObj;
  }
  return obj;
};

// Date utilities
export const formatDate = (date, format = 'YYYY-MM-DD') => {
  if (!date || !(date instanceof Date) || isNaN(date)) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  
  return format
    .replace('YYYY', String(year))
    .replace('MM', month)
    .replace('DD', day);
};

export const addDays = (date, days) => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

// Number utilities
export const formatNumber = (num, decimals = 2) => {
  if (typeof num !== 'number' || isNaN(num)) return '0';
  return num.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
};

export const clamp = (num, min, max) => {
  if (typeof num !== 'number' || typeof min !== 'number' || typeof max !== 'number') return 0;
  return Math.min(Math.max(num, min), max);
};

// Async utilities
export const delay = (ms) => {
  return new Promise(resolve => setTimeout(resolve, ms));
};

export const retry = async (fn, retries = 3, delayMs = 1000) => {
  try {
    return await fn();
  } catch (error) {
    if (retries > 0) {
      await delay(delayMs);
      return retry(fn, retries - 1, delayMs);
    }
    throw error;
  }
};

// Export error handlers
export * from './errorHandlers';
